/**
 * Settings → Account, for whoever runs the server: the people who have an account and what each may
 * use, the API key's balance, and how much market data was read today.
 */
import { useEffect, useState } from 'react'
import type { User, XaiBalance } from '@shared/types'
import { api, invitesApi, type Invite } from './api'
import { Person } from './Copy'
import { Btn, Dialog, Field, Status, Switch, askSure, cx, inputCls, usd } from './kit'
import { Group, Row, Rows, quiet, value } from './rows'
import { useDemo } from './store'

const count = (n: number) => (n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1000 ? `${Math.round(n / 1000)}k` : `${n}`)
const day = (ms: number) => new Date(ms).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
const whole = (v: string) => (v.trim() === '' ? null : Math.max(0, Math.round(Number(v))))

/** What one person may use. Empty means the usual for their role. */
function Limits({ user, me, onClose, onChanged }: { user: User; me: boolean; onClose: () => void; onChanged: () => void }) {
  const say = useDemo((s) => s.say)
  const [tokens, setTokens] = useState(user.tokenLimit != null ? `${user.tokenLimit}` : '')
  const [daily, setDaily] = useState(user.taskDailyLimit != null ? `${user.taskDailyLimit}` : '')
  const [now, setNow] = useState(user.taskOccasionalLimit != null ? `${user.taskOccasionalLimit}` : '')
  const [files, setFiles] = useState(user.maxFiles != null ? `${user.maxFiles}` : '')
  const fail = (e: unknown) => say(e instanceof Error ? e.message : 'That did not work')
  const digits = (set: (v: string) => void) => (e: { target: { value: string } }) => set(e.target.value.replace(/\D/g, ''))
  const save = () => void api.patchUser(user.id, { tokenLimit: whole(tokens), taskDailyLimit: whole(daily), taskOccasionalLimit: whole(now), maxFiles: whole(files) }).then(() => (onChanged(), onClose()), fail)
  return (
    <Dialog
      open
      onClose={onClose}
      title={user.name || user.username}
      footer={
        <>
          {!me && (
            <Btn
              variant="danger"
              className="mr-auto px-3"
              onClick={() =>
                askSure({
                  title: `Delete ${user.name || user.username}?`,
                  body: 'Their account, their agents and their conversations are deleted. This cannot be undone.',
                  cta: 'Delete account',
                  done: () => void api.deleteUser(user.id).then(() => (onChanged(), onClose()), fail),
                })
              }
            >
              Delete account
            </Btn>
          )}
          <Btn variant="outline" onPanel className="px-3" onClick={onClose}>
            Cancel
          </Btn>
          <Btn variant="white" className="px-3" onClick={save}>
            Save
          </Btn>
        </>
      }
    >
      <div className="space-y-4">
        <div className="text-[12px] leading-[18px] tracking-normal text-ws-fg-3">
          @{user.username} · {user.role === 'admin' ? 'runs the server' : 'has an account'} · {count(user.tokensIn + user.tokensOut)} tokens used so far. An empty field means the usual for their role.
        </div>
        <Field label="Tokens they may use in all" hint="Past it, their agents stop answering">
          <input value={tokens} onChange={digits(setTokens)} inputMode="numeric" placeholder="No limit" className={inputCls} />
        </Field>
        <Field label="Routines that run every day">
          <input value={daily} onChange={digits(setDaily)} inputMode="numeric" placeholder={user.role === 'admin' ? 'No limit' : '2'} className={inputCls} />
        </Field>
        <Field label="Routines that run now and then">
          <input value={now} onChange={digits(setNow)} inputMode="numeric" placeholder={user.role === 'admin' ? 'No limit' : '10'} className={inputCls} />
        </Field>
        <Field label="Files in one message">
          <input value={files} onChange={digits(setFiles)} inputMode="numeric" placeholder="10" className={inputCls} />
        </Field>
      </div>
    </Dialog>
  )
}

/** A new account made by hand (an invitation lets the person choose their own password instead). */
function AddPerson({ onClose, onChanged }: { onClose: () => void; onChanged: () => void }) {
  const say = useDemo((s) => s.say)
  const [username, setUsername] = useState('')
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [admin, setAdmin] = useState(false)
  const ok = username.trim().length > 0 && password.length >= 8
  const add = () => void api.createUser({ username: username.trim(), password, name: name.trim() || undefined, role: admin ? 'admin' : 'user' }).then(() => (onChanged(), onClose()), (e) => say(e instanceof Error ? e.message : 'That did not work'))
  return (
    <Dialog
      open
      onClose={onClose}
      title="Add someone"
      footer={
        <>
          <Btn variant="outline" onPanel className="px-3" onClick={onClose}>
            Cancel
          </Btn>
          <Btn variant="white" className="px-3" disabled={!ok} onClick={add}>
            Make the account
          </Btn>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Username">
          <input value={username} onChange={(e) => setUsername(e.target.value)} autoFocus autoComplete="off" className={inputCls} />
        </Field>
        <Field label="What they are called">
          <input value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" className={inputCls} />
        </Field>
        <Field label="Password" hint="At least 8 characters. Tell it to them yourself">
          <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" autoComplete="new-password" className={inputCls} />
        </Field>
        <div className="flex items-center justify-between gap-3">
          <span className="text-[13px] tracking-normal text-ws-fg-2">
            Runs the server with you
            <span className="block text-[12px] leading-4 text-ws-fg-3">Sees everyone, every limit and the API key’s balance</span>
          </span>
          <Switch on={admin} onChange={setAdmin} label="Runs the server with you" />
        </div>
      </div>
    </Dialog>
  )
}

/** Invitations to this server: one link lets one person in. They get a Libera of their own. */
function Invitations({ onJoined }: { onJoined: number }) {
  const say = useDemo((s) => s.say)
  const [data, setData] = useState<{ invites: Invite[]; people: { id: string; name: string; joinedAt: number }[] } | null>(null)
  const load = () => void invitesApi.list().then(setData, () => setData({ invites: [], people: [] }))
  useEffect(load, [onJoined])
  const open = (data?.invites ?? []).filter((i) => !i.usedBy && i.expiresAt > Date.now())
  const copy = (url: string) => void navigator.clipboard.writeText(url).then(() => say('Link copied — send it to them'), () => say(url))
  return (
    <Group title="Invite someone to Libera" note="One link, one person">
      <Rows>
        {open.map((i) => (
          <Row
            key={i.token}
            title={i.url.replace(/^https?:\/\//, '')}
            sub={`Not used yet · good until ${day(i.expiresAt)}`}
            right={
              <span className="flex shrink-0 items-center">
                <button type="button" className={quiet} onClick={() => copy(i.url)}>
                  Copy link
                </button>
                <button type="button" className={quiet} onClick={() => void invitesApi.revoke(i.token).then(load)}>
                  Take back
                </button>
              </span>
            }
          />
        ))}
        {(data?.people ?? []).map((p) => (
          <Row key={p.id} left={<Person name={p.name} />} title={p.name} sub={`Joined ${day(p.joinedAt)} with your invitation`} right={<Status tone="free">Has an account</Status>} />
        ))}
        <Row
          title="A new invitation"
          sub="They choose their own username and password, accept the terms themselves, and connect their own accounts"
          right={
            <button type="button" className={quiet} onClick={() => void invitesApi.create().then((i) => (load(), copy(i.url)), (e) => say(e instanceof Error ? e.message : 'That did not work'))}>
              Make a link
            </button>
          }
        />
      </Rows>
      <p className="m-0 mt-2 text-[12px] leading-[18px] tracking-normal text-ws-fg-3">An account is only an account: whether someone may copy your trades is decided on the Copy trading page, person by person.</p>
    </Group>
  )
}

const SOURCE: Record<string, string> = {
  'app:quotes': 'Prices on the screen',
  'app:extended': 'Prices outside regular hours',
  'app:ohlc': 'Charts',
  'app:technicals': 'Technicals',
  'app:profile': 'Company profiles',
  'app:news': 'News',
  'app:search': 'Looking a stock up',
  'app:workers': 'Workers and the journal',
  ai: 'Agents, in conversations',
  'ai:report': 'Agents, in reports',
  'ai:wake-task': 'Agents, in routines',
  'ai:wake-watch': 'Agents, woken by a watcher',
  'ai:wake-scan': 'Agents, reading a scan',
  'ai:wake-worker': 'Agents, called by a worker',
  watch: 'Watchers',
  scan: 'Scans',
  snapshot: 'Portfolio snapshots',
  other: 'Other',
}
type Usage = Awaited<ReturnType<typeof api.eodhdUsage>>

export function People() {
  const me = useDemo((s) => s.user?.id)
  const [users, setUsers] = useState<User[] | null>(null)
  const [bal, setBal] = useState<XaiBalance | null>(null)
  const [data, setData] = useState<Usage | null>(null)
  const [open, setOpen] = useState<string | null>(null)
  const [add, setAdd] = useState(false)
  const load = () => void api.listUsers().then(setUsers, () => setUsers([]))
  useEffect(() => {
    load()
    void api.xaiBalance().then(setBal, () => {})
    const read = () => void api.eodhdUsage().then(setData, () => {})
    read()
    const t = window.setInterval(read, 60_000)
    return () => window.clearInterval(t)
  }, [])
  const user = users?.find((u) => u.id === open)
  const a = data?.account
  const cap = a ? a.limit + a.extra : 0
  const used = a && cap ? Math.min(100, Math.round((a.used / cap) * 100)) : 0
  const rows = data ? Object.entries(data.today.bySource).sort((x, y) => y[1] - x[1]) : []
  return (
    <>
      <Group title="People" note="Only you see this">
        <Rows>
          {(users ?? []).map((u) => (
            <Row
              key={u.id}
              left={<Person name={u.name || u.username} />}
              title={u.name || u.username}
              sub={`@${u.username}${u.role === 'admin' ? ' · runs the server' : ''} · ${count(u.tokensIn + u.tokensOut)} tokens${u.tokenLimit != null ? ` of ${count(u.tokenLimit)}` : ''}${u.lastLogin ? ` · last here ${day(u.lastLogin)}` : ' · never signed in'}`}
              right={
                <button type="button" className={quiet} onClick={() => setOpen(u.id)}>
                  Limits
                </button>
              }
            />
          ))}
          <Row
            title="Add someone"
            sub="An account with a password you choose — an invitation lets them choose their own"
            right={
              <button type="button" className={quiet} onClick={() => setAdd(true)}>
                Add
              </button>
            }
          />
        </Rows>
      </Group>

      <Invitations onJoined={users?.length ?? 0} />

      {bal?.configured && (
        <Group title="The server’s API key" note="Pays when a Grok plan cannot">
          <Rows>
            <Row title="Balance" sub={bal.ok ? undefined : (bal.error ?? 'xAI did not answer')} right={<span className={value}>{bal.balance != null ? usd(bal.balance) : '—'}</span>} />
            {bal.spentThisCycle != null && <Row title="Used this billing cycle" sub={bal.owed != null ? `${usd(bal.owed)} is owed after prepaid credit` : undefined} right={<span className={value}>{usd(bal.spentThisCycle)}</span>} />}
          </Rows>
        </Group>
      )}

      <Group title="Market data today" note="Resets at midnight GMT">
        <div className="rounded-[8px] border border-ws-line px-4 pb-3 pt-[14px]">
          <div className="flex items-baseline gap-2">
            <span className="text-[20px] font-medium leading-7 tracking-[-0.01em] tabular-nums text-ws-fg">{a ? a.used.toLocaleString('en-US') : data ? data.today.total.toLocaleString('en-US') : '—'}</span>
            <span className="text-[13px] tracking-normal text-ws-fg-2">{a ? `of ${cap.toLocaleString('en-US')} calls` : 'calls counted here'}</span>
            {a && <span className="ml-auto text-[12px] tracking-normal text-ws-fg-3">{used}% used</span>}
          </div>
          {a && (
            <div className="mt-3 h-[6px] overflow-hidden rounded-[3px] bg-ws-edge" role="img" aria-label={`${used}% of today’s market data is used`}>
              <div className={cx('h-full rounded-[3px]', used >= 90 ? 'bg-ws-down' : 'bg-ws-fg')} style={{ width: `${used}%` }} />
            </div>
          )}
          {rows.length > 0 && (
            <dl className="m-0 mt-3 divide-y divide-ws-hair border-t border-ws-hair">
              {rows.map(([k, n]) => (
                <div key={k} className="flex h-8 items-center justify-between text-[13px] tracking-normal">
                  <dt className="text-ws-fg-2">{SOURCE[k] ?? k}</dt>
                  <dd className="m-0 tabular-nums text-ws-fg-3">{n.toLocaleString('en-US')}</dd>
                </div>
              ))}
            </dl>
          )}
          <p className="m-0 mt-3 text-[12px] leading-[18px] tracking-normal text-ws-fg-3">A live price costs one call for each stock; intraday bars, news and screens cost 5; fundamentals 10.</p>
        </div>
      </Group>

      {user && <Limits user={user} me={user.id === me} onClose={() => setOpen(null)} onChanged={load} />}
      {add && <AddPerson onClose={() => setAdd(false)} onChanged={load} />}
    </>
  )
}
